import { computed, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { unique } from "@/lib/collections";

type SignedColumnId<Id extends string> = Id | `-${Id}`;

interface OrderingOptions<ColumnId extends string> {
  default: SignedColumnId<ColumnId>;
}

interface OrderingConfig<ColumnId extends string> {
  id<K extends ColumnId>(columnId: K): K;
  use(options: OrderingOptions<ColumnId>): Ref<SignedColumnId<ColumnId>>;
}

interface CreateOrderingOptions {
  /**
   * Dynamic prefixes that are valid for ordering (e.g., "metadata:" for
   * `metadata:some_key`).
   */
  dynamicPrefixes?: string[];
}

export function createOrderingFromValues<T extends string>(
  values: T[],
  options?: CreateOrderingOptions
) {
  const unsigned = unique(
    values.map((v) => (v.startsWith("-") ? v.slice(1) : v))
  ) as Array<T extends `-${infer U}` ? U : T>;
  return createOrdering(unsigned, options);
}

export function createOrdering<ColumnId extends string>(
  columnIds: ColumnId[],
  options: CreateOrderingOptions = {}
): OrderingConfig<ColumnId> {
  const validColumnIds = new Set(columnIds);
  const { dynamicPrefixes = [] } = options;

  const isValidOrdering = (
    value: string
  ): value is SignedColumnId<ColumnId> => {
    const isDescending = value.startsWith("-");
    const columnId = isDescending ? value.slice(1) : value;

    if (validColumnIds.has(columnId as ColumnId)) {
      return true;
    }

    // Check if the column ID matches any dynamic prefix
    for (const prefix of dynamicPrefixes) {
      if (columnId.startsWith(prefix)) {
        return true;
      }
    }

    return false;
  };

  return {
    id(columnId) {
      return columnId;
    },
    use({ default: defaultValue }) {
      const router = useRouter();
      const currentRoute = useRoute();

      const ordering = computed({
        get(): SignedColumnId<ColumnId> {
          const raw = currentRoute.query.order;

          if (typeof raw === "string" && isValidOrdering(raw)) {
            return raw;
          }

          return defaultValue;
        },
        set(next: SignedColumnId<ColumnId> | undefined) {
          if (next === ordering.value) {
            return;
          }

          if (next !== undefined && !isValidOrdering(next)) {
            return;
          }

          router.push({ query: { ...currentRoute.query, order: next } });
        },
      });

      return ordering;
    },
  };
}
